import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {unwrapResult} from '@reduxjs/toolkit';
import {useAppDispatch, useNavigate} from 'hooks';
import {pages} from 'paths';
import {pamApi} from 'api';
import {useToastNotifications} from 'toast-notifications';
import {
  pageLoadingSelector,
  fileDetailsSelector,
  fileDownloadingSelector,
  fetchFileByIdThunk,
  deleteFileThunk,
  updateResourcesFileThunk,
  updateScormResourceThunk,
  updateWebLinkResourceThunk,
  downloadFileByIdThunk,
} from '../store';
import {IFileDetails, IGetFileApiResponse, IResourceMetadata} from 'models/resources';
import {useFileIdParam} from '.';
import {IEditFileMetadataFormBaseValues} from '../components/FilesEditResourceForm/config';
import {mapUpdateResourceFilePayload, mapUpdateResourceScormPayload, mapUpdateResourceWebLinkPayload} from '../utils';
import {createLinkForDownload} from 'utils';

export const useFileEdit = () => {
  const {push} = useNavigate();
  const dispatch = useAppDispatch();
  const {fileId} = useFileIdParam();
  const {addToastNotification} = useToastNotifications();

  const loading = useSelector(pageLoadingSelector);
  const fileDownloading = useSelector(fileDownloadingSelector);
  const fileDetails = useSelector(fileDetailsSelector) as IFileDetails;
  const fileMetadata = fileDetails?.metadata as IResourceMetadata;

  const fetchFileById: (id: string) => Promise<IGetFileApiResponse> = useCallback(
    (id: string) => dispatch(fetchFileByIdThunk({id, push})),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const redirectToFilesAllPage: () => void = useCallback(() => {
    push(`${pages.adminPortal.files.root}`);
  }, [push]);

  const redirectToEditLearningPathPage: (id: string) => void = useCallback(
    (id) => window.open(`${pages.adminPortal.learningPaths.editLink}/${id}`, '_blank'),
    []
  );

  const deleteFile: () => void = useCallback(
    () =>
      dispatch(deleteFileThunk(fileId))
        .then(unwrapResult)
        .then(() => redirectToFilesAllPage()),
    [dispatch, fileId, redirectToFilesAllPage]
  );

  const updateFile: (formValues: IEditFileMetadataFormBaseValues) => Promise<IGetFileApiResponse> = useCallback(
    (formValues: IEditFileMetadataFormBaseValues) => {
      const payload = mapUpdateResourceFilePayload(fileId, formValues);
      return dispatch(updateResourcesFileThunk(payload));
    },
    [dispatch, fileId]
  );

  const updateScorm: (formValues: IEditFileMetadataFormBaseValues) => Promise<IGetFileApiResponse> = useCallback(
    (formValues: IEditFileMetadataFormBaseValues) => {
      const payload = mapUpdateResourceScormPayload(fileId, formValues);
      return dispatch(updateScormResourceThunk(payload))
        .then(unwrapResult)
        .then(() => {
          // Redirect only if there is a new version of the file
          if (formValues?.file) {
            return redirectToFilesAllPage();
          }
        });
    },
    [dispatch, fileId, redirectToFilesAllPage]
  );

  const updateWebLink: (formValues: IEditFileMetadataFormBaseValues) => Promise<IGetFileApiResponse> = useCallback(
    (formValues: IEditFileMetadataFormBaseValues) => {
      const payload = mapUpdateResourceWebLinkPayload(fileId, formValues);
      return dispatch(updateWebLinkResourceThunk(payload));
    },
    [dispatch, fileId]
  );

  const updateResource = useCallback(
    (
      formValues: IEditFileMetadataFormBaseValues,
      fileType: {isResource: boolean; isScormPackage: boolean; isWebLink: boolean}
    ) => {
      if (fileType.isResource) {
        return updateFile(formValues);
      }

      if (fileType.isScormPackage) {
        return updateScorm(formValues);
      }

      if (fileType.isWebLink) {
        return updateWebLink(formValues);
      }
    },
    [updateFile, updateScorm, updateWebLink]
  );

  const downloadFileById = useCallback(
    (id: string) =>
      dispatch(downloadFileByIdThunk(id))
        .then(unwrapResult)
        .then(({blob, fileName}) => {
          const objectUrl = window.URL.createObjectURL(blob);
          createLinkForDownload(objectUrl, fileName);
          // Freeing resources in memory
          window.URL.revokeObjectURL(objectUrl);
          // Notify the user
          // dispatch(toastNotificationManager.createSuccessToastAction(fileEditMessages.fileDownload));
        }),
    [dispatch]
  );

  const copyCurrentYearExpirableLinkHandler = useCallback(
    async (resourceUrl: string) => {
      try {
        if (!resourceUrl) {
          console.error('No resource URL provided');
          addToastNotification('Failed to create expirable link - No resource URL provided');
          return;
        }

        // For "current year" button: always request current year from API (8/15/2026)
        const payload = {
          resourceUrl,
          isCurrentYear: true,
        };
        console.log('🟠 CURRENT YEAR - Payload being sent:', payload);
        const response = await pamApi.createShortenToUrlExpiration(payload);
        console.log('🟠 CURRENT YEAR - Response received:', response?.parsedBody);

        // Check if the API call was successful
        if (!response?.ok) {
          const errorMsg = `API returned status ${response?.status}: ${response?.statusText}`;
          console.error(errorMsg);
          addToastNotification(`Failed to create expirable link - ${errorMsg}`);
          return;
        }

        // Extract the short URL from the response
        let shortUrl = null;
        const responseBody = response.parsedBody as any;

        // Check if the response is a plain string URL
        if (typeof responseBody === 'string' && responseBody.startsWith('http')) {
          shortUrl = responseBody;
        } else if (responseBody) {
          // Check various possible JSON response structures
          shortUrl =
            responseBody.data?.shortUrl ||
            responseBody.shortUrl ||
            responseBody.url ||
            responseBody.data?.url ||
            responseBody.result?.shortUrl ||
            responseBody.result?.url;
        }

        if (!shortUrl) {
          console.error('No shortUrl found in API response');
          addToastNotification('Failed to create expirable link - no URL returned from API');
          return;
        }

        // Copy to clipboard and show success message
        console.log('🟠 CURRENT YEAR - About to copy to clipboard:', shortUrl);
        try {
          // Check if clipboard API is available
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(shortUrl);
            console.log('🟠 CURRENT YEAR - Successfully copied to clipboard using modern API');
          } else {
            // Fallback method for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = shortUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            console.log('🟠 CURRENT YEAR - Successfully copied to clipboard using fallback method');
          }
          addToastNotification('Current year expirable link copied to clipboard');
        } catch (clipboardError) {
          console.error('🟠 CURRENT YEAR - Clipboard error:', clipboardError);
          // Try fallback method if modern API fails
          try {
            const textArea = document.createElement('textarea');
            textArea.value = shortUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            console.log('🟠 CURRENT YEAR - Successfully copied using fallback after modern API failed');
            addToastNotification('Current year expirable link copied to clipboard');
          } catch (fallbackError) {
            console.error('🟠 CURRENT YEAR - Both clipboard methods failed:', fallbackError);
            addToastNotification('Failed to copy to clipboard - please copy manually: ' + shortUrl);
          }
        }
      } catch (error) {
        console.error('Error creating expirable link:', error);

        let errorMessage = 'Failed to create expirable link';
        if (error?.response?.status) {
          errorMessage += ` (${error.response.status})`;
        }

        addToastNotification(errorMessage);
      }
    },
    [addToastNotification]
  );

  const copyNextYearExpirableLinkHandler = useCallback(
    async (resourceUrl: string) => {
      try {
        if (!resourceUrl) {
          console.error('No resource URL provided');
          addToastNotification('Failed to create expirable link - No resource URL provided');
          return;
        }

        // Calculate isCurrentYear based on August 15th cutoff logic
        // This ensures the API gets the correct flag for the year we want to display
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const august15CurrentYear = new Date(currentYear, 7, 15); // Month is 0-indexed, so 7 = August

        // For "next year" button:
        // Before Aug 15: we want next year (2026) -> isCurrentYear: false
        // After Aug 15: we want year+2 (2027) -> but API only supports current/next year
        // LIMITATION: API doesn't support year+2, so after Aug 15 this will only give year+1
        const isCurrentYear = false; // Always request next year from API

        const payload = {
          resourceUrl,
          isCurrentYear,
        };
        console.log('🟡 NEXT YEAR - Payload being sent:', payload);

        const response = await pamApi.createShortenToUrlExpiration(payload);
        console.log('🟡 NEXT YEAR - Response received:', response?.parsedBody);

        // Check if the API call was successful
        if (!response?.ok) {
          const errorMsg = `API returned status ${response?.status}: ${response?.statusText}`;
          console.error(errorMsg);
          addToastNotification(`Failed to create expirable link - ${errorMsg}`);
          return;
        }

        // Extract the short URL from the response
        let shortUrl = null;
        const responseBody = response.parsedBody as any;

        // Check if the response is a plain string URL
        if (typeof responseBody === 'string' && responseBody.startsWith('http')) {
          shortUrl = responseBody;
        } else if (responseBody) {
          // Check various possible JSON response structures
          shortUrl =
            responseBody.data?.shortUrl ||
            responseBody.shortUrl ||
            responseBody.url ||
            responseBody.data?.url ||
            responseBody.result?.shortUrl ||
            responseBody.result?.url;
        }

        if (!shortUrl) {
          console.error('No shortUrl found in API response');
          addToastNotification('Failed to create expirable link - no URL returned from API');
          return;
        }

        // Copy to clipboard and show success message
        console.log('🟡 NEXT YEAR - About to copy to clipboard:', shortUrl);
        try {
          // Check if clipboard API is available
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(shortUrl);
            console.log('🟡 NEXT YEAR - Successfully copied to clipboard using modern API');
          } else {
            // Fallback method for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = shortUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            console.log('🟡 NEXT YEAR - Successfully copied to clipboard using fallback method');
          }
          addToastNotification('Next year expirable link copied to clipboard');
        } catch (clipboardError) {
          console.error('🟡 NEXT YEAR - Clipboard error:', clipboardError);
          // Try fallback method if modern API fails
          try {
            const textArea = document.createElement('textarea');
            textArea.value = shortUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            console.log('🟡 NEXT YEAR - Successfully copied using fallback after modern API failed');
            addToastNotification('Next year expirable link copied to clipboard');
          } catch (fallbackError) {
            console.error('🟡 NEXT YEAR - Both clipboard methods failed:', fallbackError);
            addToastNotification('Failed to copy to clipboard - please copy manually: ' + shortUrl);
          }
        }
      } catch (error) {
        console.error('Error creating next year expirable link:', error);

        let errorMessage = 'Failed to create expirable link';
        if (error?.response?.status) {
          errorMessage += ` (${error.response.status})`;
        }

        addToastNotification(errorMessage);
      }
    },
    [addToastNotification]
  );

  return {
    loading,
    fileDownloading,
    fileDetails,
    fileMetadata,
    fetchFileById,
    linkedLearningPaths: fileDetails?.relatedLearningPaths,
    status: fileDetails?.details?.status,
    userDetails: fileDetails?.details,

    updateResource,
    updateFile,
    deleteFile,
    redirectToFilesAllPage,
    redirectToEditLearningPathPage,
    downloadFileById,
    copyCurrentYearExpirableLinkHandler,
    copyNextYearExpirableLinkHandler,
  };
};
